import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ProjectQueries, PROJECT_QUERY_KEYS } from './query';
import {
  DivisionAndDistrict,
  GetAllowedRequestTypesResponseData,
  UseAllowedRequestTypesForLocationParams,
  UseGetAllowedRequestTypesParams,
} from './types';
import { useLookupTableDivisionsAndDistricts } from '../lookup/hooks';

export const useGetAllowedRequestTypes = ({ state, district, enabled = true }: UseGetAllowedRequestTypesParams) => {
  return useQuery<GetAllowedRequestTypesResponseData>({
    queryKey: [PROJECT_QUERY_KEYS.GET_ALLOWED_REQUEST_TYPES, state, district],
    queryFn: () =>
      ProjectQueries.getAllowedRequestTypes({
        state,
        district,
      }),
    enabled,
  });
};

export const useAllowedRequestTypesForLocation = ({
  requestLocationState,
  requestLocationDistrict,
  enabled = true,
}: UseAllowedRequestTypesForLocationParams) => {
  const requestState = requestLocationState;
  const divisionDistrictLookup = useLookupTableDivisionsAndDistricts() as DivisionAndDistrict[];

  const district = useMemo(() => {
    if (!requestLocationDistrict) return undefined;

    // Ensure the value matches orgName format ("X District"), then map to the BE shortName
    const districtOrgName = requestLocationDistrict.endsWith('District')
      ? requestLocationDistrict
      : `${requestLocationDistrict} District`;

    return divisionDistrictLookup.find((item) => item.orgName === districtOrgName)?.shortName;
  }, [requestLocationDistrict, divisionDistrictLookup]);

  const hasValidDistrict = Boolean(district);
  const hasValidState = Boolean(requestState);

  const query = useGetAllowedRequestTypes({
    state: requestState,
    district,
    enabled: enabled && (hasValidDistrict || hasValidState),
  });

  return useMemo(
    () => ({
      allowedRequestTypes: query.data ?? {},
      requestState,
      district,
      isLoading: query.isLoading,
      isFetching: query.isFetching,
      isError: query.isError,
    }),
    [query.data, query.isLoading, query.isFetching, query.isError, requestState, district]
  );
};
